import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { 
  Plane, Clock, Shield, Star, Phone, MapPin, 
  CheckCircle, ArrowRight, Users, Car, Briefcase, Building2
} from 'lucide-react';

const FLEET_IMG = 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/kyn2ki0d_image1.jpeg';
const ESCALADE_IMG = 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg';
const ARLINGTON_IMG = 'https://images.pexels.com/photos/16156727/pexels-photo-16156727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const schema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Capital Chauffeur Services - Arlington",
  "telephone": "+1-202-684-0534",
  "email": "contact@capitalchauffeurservices.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Arlington",
    "addressRegion": "VA",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.8816",
    "longitude": "-77.0910"
  },
  "priceRange": "$$$",
  "image": FLEET_IMG,
  "description": "Premium black car service in Arlington, Virginia. Airport transfers, corporate chauffeur, event transportation with luxury Cadillac Escalade fleet.",
  "areaServed": ["Arlington", "Pentagon City", "Crystal City", "Rosslyn", "Clarendon", "National Landing"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Black Car Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Airport Car Service" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Corporate Chauffeur" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Event Transportation" } }
    ]
  }
};

export default function ArlingtonPage() {
  return (
    <div data-testid="arlington-page" className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Black Car Service Arlington VA | Luxury Chauffeur | Capital Chauffeur Services"
        description="Luxury black car service in Arlington, Virginia. Airport transfers, corporate chauffeur, event transportation. Premium Escalade fleet. Book today."
        canonical="/black-car-service-arlington"
        schema={schema}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ESCALADE_IMG}
            alt="Black car service Arlington Virginia luxury Cadillac Escalade"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">Arlington, Virginia</p>
          <h1 data-testid="arlington-h1" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Black Car Service<br />in Arlington, Virginia
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl mb-8 leading-relaxed">
            Premium luxury transportation in the heart of Arlington. From the Pentagon to Rosslyn, Crystal City to Clarendon, our black car service delivers executive-class travel across Arlington County.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase">
                Book Your Ride
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Arlington */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">About Our Arlington Service</p>
            <h2 className="text-3xl font-serif text-white mb-6">Arlington's Premier Black Car Service</h2>
            <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
              <p>
                Arlington, Virginia is one of the most dynamic counties in the United States. Home to the Pentagon, Arlington National Cemetery, and a rapidly growing tech corridor known as National Landing — the future headquarters of Amazon's HQ2 — Arlington attracts government leaders, tech executives, military officials, and international professionals who demand premium transportation.
              </p>
              <p>
                Capital Chauffeur Services has built its reputation serving Arlington's most discerning travelers. Whether you need a ride from your Rosslyn high-rise to Reagan National Airport, a corporate shuttle from Pentagon City to Capitol Hill, or evening transportation to a gala in Georgetown, our professional chauffeurs and premium black Escalade fleet are at your service.
              </p>
              <p>
                Arlington's strategic location — directly across the Potomac from downtown Washington, DC — means our clients enjoy quick access to both the capital and Northern Virginia's business centers. Our chauffeurs are experts in navigating Arlington's corridors, from the Route 50 interchange to the George Washington Parkway.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={ARLINGTON_IMG}
              alt="Arlington Virginia National Cemetery autumn scenic view near black car service area"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-white mb-4">Arlington Black Car Services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Plane, title: 'Airport Transfers', desc: 'Quick transfers from Arlington to Reagan National (10 min), Dulles (30 min), or BWI Airport. Flight monitoring and curbside pickup included.' },
              { icon: Briefcase, title: 'Pentagon & Government', desc: 'Professional transportation for Pentagon personnel, government contractors, and officials. Discreet service with security-conscious chauffeurs.' },
              { icon: Building2, title: 'National Landing & Tech', desc: 'Serving Arlington\'s tech corridor including Amazon HQ2, Deloitte, and the growing National Landing business district.' },
              { icon: Clock, title: 'Hourly Chauffeur Service', desc: 'Book your chauffeur by the hour for multi-stop business days across Arlington and the DC metro. Your driver waits between meetings.' },
              { icon: Star, title: 'Evening & Event Service', desc: 'Red carpet transportation for Arlington galas, dinners at The Ritz-Carlton Pentagon City, and performances at Signature Theatre.' },
              { icon: Users, title: 'Residential Pickup', desc: 'Door-to-door service from Arlington neighborhoods — Ballston, Virginia Square, Courthouse, Lyon Village, and beyond.' },
            ].map((service, i) => (
              <div key={i} className="border border-white/10 p-8 hover:border-[#D4AF37]/30 transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="text-white font-serif text-lg mb-3">{service.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-4 text-center">Arlington Neighborhoods We Serve</h2>
          <p className="text-[#A3A3A3] text-center mb-10 max-w-2xl mx-auto">
            Our black car service covers every corner of Arlington County and connects seamlessly to DC and the wider NoVA region.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Rosslyn', 'Pentagon City', 'Crystal City', 'National Landing',
              'Clarendon', 'Ballston', 'Courthouse', 'Virginia Square',
              'Shirlington', 'Columbia Pike', 'Cherrydale', 'Lyon Village',
            ].map((area, i) => (
              <div key={i} className="flex items-center gap-3 border border-white/10 p-4">
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white text-sm">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden">
            <img
              src={FLEET_IMG}
              alt="Capital Chauffeur Services luxury fleet for Arlington Virginia black car service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-10 text-center">Why Arlington Residents and Businesses Choose Us</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Local expertise — our chauffeurs know Arlington\'s streets, shortcuts, and traffic patterns intimately',
              'Minutes from Reagan National Airport — the fastest airport transfers in the DC metro area',
              'Trusted by Pentagon officials, defense contractors, and government agencies for secure transportation',
              'Premium black Cadillac Escalade ESV with leather interiors, tinted windows, and complimentary amenities',
              'Available 24/7 for early morning departures, late-night arrivals, and everything in between',
              'Competitive flat-rate pricing between Arlington and all major DC-area destinations',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-[#A3A3A3] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Explore Our Other Services</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            {[
              { label: 'Dulles Airport Car Service', path: '/dulles-airport-car-service' },
              { label: 'Reagan Airport Car Service', path: '/reagan-airport-car-service' },
              { label: 'Corporate Chauffeur NoVA', path: '/corporate-chauffeur-northern-virginia' },
              { label: 'Black Car Service Tysons', path: '/black-car-service-tysons' },
              { label: 'All Services', path: '/services' },
            ].map((link, i) => (
              <Link key={i} to={link.path}>
                <Button variant="outline" className="border-white/20 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-none px-6 py-3 text-xs tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                  {link.label} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Book Your Arlington Black Car Service</h2>
          <p className="text-[#A3A3A3] mb-8">
            Experience the difference of a dedicated chauffeur service in Arlington, Virginia. Premium vehicles, professional drivers, flawless service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase">
                Book Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
